import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["feedbackUrl", "preQueryItemSupplier", "postQueryItemSupplier", "generateAdvancedSearchUrl", "features", "resultOnClick"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { messages } from '../../../messages';
import { messages as trelloMessages } from './messages';
import { SearchDialogProduct } from '../../product-router';
import { useDefaultSuppliers } from '../../result-supplier';
import { TrelloSearchResultComponent } from './trello-search-result';
import { TrelloFooter } from './trello-footer';
import { TRELLO_PRODUCT_ID, TrelloScope } from './types';
import { BoardIcon, CardIcon } from './icons';
import { viewAllLinkGenerator } from './url-generators';
import { useSearchSessionId } from '../../../common/search-session-provider';
import { addQueryParams } from '../../../utils/url-utils';
var TrelloBodyAsync = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "@atlassian/product-search-dialog/extensible/trello/trello-body" */'./trello-body').then(function (_ref) {
    var TrelloBody = _ref.TrelloBody;
    return {
      default: TrelloBody
    };
  });
});
export var TrelloTab = function TrelloTab(_ref2) {
  var feedbackUrl = _ref2.feedbackUrl,
    preQueryItemSupplier = _ref2.preQueryItemSupplier,
    postQueryItemSupplier = _ref2.postQueryItemSupplier,
    generateAdvancedSearchUrl = _ref2.generateAdvancedSearchUrl,
    features = _ref2.features,
    resultOnClick = _ref2.resultOnClick,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var searchSessionId = useSearchSessionId();
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var trelloGenerateAdvancedSearchUrl = useCallback(function (query) {
    return addQueryParams(viewAllLinkGenerator(query), {
      search_id: searchSessionId
    });
  }, [searchSessionId]);
  var boardSection = useMemo(function () {
    return {
      id: TrelloScope.Board,
      title: formatMessage(trelloMessages.trello_boards_heading),
      scope: TrelloScope.Board,
      showResultCount: false,
      resultMapper: function resultMapper(_ref3) {
        var results = _ref3.results;
        return results.map(function (board) {
          var _board$organization$d, _board$organization, _board$idOrganization;
          return {
            id: board.id,
            title: board.name,
            meta: (_board$organization$d = (_board$organization = board.organization) === null || _board$organization === void 0 ? void 0 : _board$organization.displayName) !== null && _board$organization$d !== void 0 ? _board$organization$d : '',
            closed: board.closed,
            url: board.url,
            prefs: board.prefs,
            icon: /*#__PURE__*/React.createElement(BoardIcon, board),
            containerId: (_board$idOrganization = board.idOrganization) !== null && _board$idOrganization !== void 0 ? _board$idOrganization : undefined
          };
        });
      },
      resultRenderer: function resultRenderer(args) {
        var _args$searchResult = args === null || args === void 0 ? void 0 : args.searchResult,
          id = _args$searchResult.id,
          title = _args$searchResult.title,
          meta = _args$searchResult.meta,
          closed = _args$searchResult.closed,
          url = _args$searchResult.url,
          prefs = _args$searchResult.prefs;
        return /*#__PURE__*/React.createElement(TrelloSearchResultComponent, {
          id: id,
          title: title,
          meta: {
            title: formatMessage(trelloMessages.trello_workspace),
            value: meta
          },
          closed: closed,
          url: url,
          prefs: prefs,
          isPreviewOnTrelloSearchResultsEnabled: features === null || features === void 0 ? void 0 : features.isPreviewOnTrelloSearchResultsEnabled,
          isColorBlindModeEnabled: features === null || features === void 0 ? void 0 : features.isColorBlindModeEnabled,
          screen: args === null || args === void 0 ? void 0 : args.screen,
          onResultClick: resultOnClick
        });
      }
    };
  }, [features === null || features === void 0 ? void 0 : features.isColorBlindModeEnabled, features === null || features === void 0 ? void 0 : features.isPreviewOnTrelloSearchResultsEnabled, formatMessage, resultOnClick]);
  var cardSection = useMemo(function () {
    return {
      id: TrelloScope.Card,
      title: formatMessage(trelloMessages.trello_cards_heading),
      scope: TrelloScope.Card,
      showResultCount: false,
      resultMapper: function resultMapper(_ref4) {
        var results = _ref4.results;
        return results.map(function (card) {
          return {
            id: card.id,
            title: card.name,
            meta: card.board.name,
            additionalMeta: card.list.name,
            closed: card.closed,
            url: card.url,
            icon: /*#__PURE__*/React.createElement(CardIcon, null),
            containerId: card.idBoard
          };
        });
      },
      resultRenderer: function resultRenderer(args) {
        var _args$searchResult2 = args === null || args === void 0 ? void 0 : args.searchResult,
          id = _args$searchResult2.id,
          title = _args$searchResult2.title,
          meta = _args$searchResult2.meta,
          additionalMeta = _args$searchResult2.additionalMeta,
          url = _args$searchResult2.url,
          closed = _args$searchResult2.closed;
        return /*#__PURE__*/React.createElement(TrelloSearchResultComponent, {
          id: id,
          title: title,
          meta: {
            title: formatMessage(trelloMessages.trello_board),
            value: meta
          },
          additionalMeta: {
            title: formatMessage(trelloMessages.trello_list),
            value: additionalMeta
          },
          closed: closed,
          url: url,
          isPreviewOnTrelloSearchResultsEnabled: features === null || features === void 0 ? void 0 : features.isPreviewOnTrelloSearchResultsEnabled,
          isColorBlindModeEnabled: features === null || features === void 0 ? void 0 : features.isColorBlindModeEnabled,
          screen: args === null || args === void 0 ? void 0 : args.screen,
          onResultClick: resultOnClick
        });
      }
    };
  }, [features === null || features === void 0 ? void 0 : features.isColorBlindModeEnabled, features === null || features === void 0 ? void 0 : features.isPreviewOnTrelloSearchResultsEnabled, formatMessage, resultOnClick]);
  var sections = useMemo(function () {
    return features !== null && features !== void 0 && features.isCardResultsSectionFirst ? [cardSection, _objectSpread(_objectSpread({}, boardSection), {}, {
      viewAllLinkGenerator: trelloGenerateAdvancedSearchUrl
    })] : [boardSection, _objectSpread(_objectSpread({}, cardSection), {}, {
      viewAllLinkGenerator: trelloGenerateAdvancedSearchUrl
    })];
  }, [boardSection, cardSection, features === null || features === void 0 ? void 0 : features.isCardResultsSectionFirst, trelloGenerateAdvancedSearchUrl]);
  var itemSuppliers = useDefaultSuppliers(TRELLO_PRODUCT_ID, sections, {
    preQueryItemSupplier: preQueryItemSupplier,
    postQueryItemSupplier: postQueryItemSupplier
  });
  var preQuerySectionTitleGenerator = useCallback(function (section) {
    return section.id === TrelloScope.Board ? formatMessage(trelloMessages.trello_recent_boards_heading) : '';
  }, [formatMessage]);
  var Footer = useMemo(function () {
    return feedbackUrl ? function () {
      return /*#__PURE__*/React.createElement(TrelloFooter, {
        feedbackUrl: feedbackUrl,
        urlGeneratorForNoResultsScreen: trelloGenerateAdvancedSearchUrl
      });
    } : undefined;
  }, [feedbackUrl, trelloGenerateAdvancedSearchUrl]);
  return /*#__PURE__*/React.createElement(SearchDialogProduct, _extends({
    id: TRELLO_PRODUCT_ID,
    expandedStateInputPlaceholder: formatMessage(trelloMessages.trello_expanded_input_placeholder),
    preQuerySectionTitleGenerator: preQuerySectionTitleGenerator,
    generateAdvancedSearchUrl: trelloGenerateAdvancedSearchUrl,
    sections: sections,
    title: formatMessage(messages.trello_tab_label)
  }, rest, itemSuppliers), function (props) {
    return features !== null && features !== void 0 && features.isCardResultsSectionFirst ? {
      Footer: Footer,
      Body: function Body() {
        return /*#__PURE__*/React.createElement(TrelloBodyAsync, props);
      }
    } : {
      Footer: Footer
    };
  });
};